// Validation utility functions that can be used with React 19 hooks
// These don't use hooks, so they can be called inside callbacks

export interface ValidationError {
  [key: string]: string;
}

export const validateEmail = (email: string, t?: (key: string) => string): string | null => {
  if (!email) {
    return t ? t('errors.validation.required') : 'Email is required';
  }
  if (!/\S+@\S+\.\S+/.test(email)) {
    return t ? t('errors.validation.email') : 'Invalid email format';
  }
  return null;
};

export const validateUsernameOrEmail = (usernameOrEmail: string, t?: (key: string) => string): string | null => {
  if (!usernameOrEmail) {
    return t ? t('errors.validation.usernameOrEmail') : 'Username or email is required';
  }
  // Usernames are free text when staff are created (they may contain spaces,
  // accents or capitals), so only require something to be typed: the backend
  // decides whether it matches a username or an email.
  if (!usernameOrEmail.trim()) {
    return t ? t('errors.validation.usernameOrEmail') : 'Please enter a valid username or email';
  }
  return null;
};

export const validateRequired = (value: string, fieldName: string, t?: (key: string) => string): string | null => {
  if (!value || value.trim() === '') {
    return t ? t('errors.validation.required') : `${fieldName} is required`;
  }
  return null;
};

export const validateDateOfBirth = (dateOfBirth: string, minAge: number = 18, t?: (key: string) => string): string | null => {
  if (!dateOfBirth) {
    return t ? t('errors.validation.required') : 'Date of birth is required';
  }

  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  
  // Check if the date is valid
  if (isNaN(birthDate.getTime())) {
    return t ? t('errors.validation.invalidDate') : 'Please enter a valid date';
  }

  // Check if the date is in the future
  if (birthDate > today) {
    return t ? t('errors.validation.futureDate') : 'Date of birth cannot be in the future';
  }

  // Calculate age
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Adjust age if birthday hasn't occurred this year
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
    ? age - 1 
    : age;

  if (actualAge < minAge) {
    return t ? t('errors.validation.minAge').replace('{{minAge}}', minAge.toString()) : `You must be at least ${minAge} years old`;
  }

  return null;
};

export const validatePassword = (password: string, t?: (key: string) => string): string | null => {
  if (!password) {
    return t ? t('errors.validation.required') : 'Password is required';
  }
  if (password.length < 6) {
    return t ? t('errors.validation.password') : 'Password must be at least 6 characters';
  }
  return null;
};

export const validatePasswordMatch = (password: string, confirmPassword: string, t?: (key: string) => string): string | null => {
  if (!confirmPassword) {
    return t ? t('errors.validation.required') : 'Please confirm your password';
  }
  if (password !== confirmPassword) {
    return t ? t('errors.validation.passwordMatch') : 'Passwords do not match';
  }
  return null;
};

export const validateRegistrationForm = (formData: {
  email: string;
  dateOfBirth: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}, t?: (key: string) => string): ValidationError => {
  const errors: ValidationError = {};

  // Email validation
  const emailError = validateEmail(formData.email, t);
  if (emailError) errors.email = emailError;

  // Date of birth validation
  const dateOfBirthError = validateDateOfBirth(formData.dateOfBirth, 18, t);
  if (dateOfBirthError) errors.dateOfBirth = dateOfBirthError;

  // Password validation
  const passwordError = validatePassword(formData.password, t);
  if (passwordError) errors.password = passwordError;

  // Password confirmation validation
  const confirmPasswordError = validatePasswordMatch(formData.password, formData.confirmPassword, t);
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;

  // Terms and conditions validation
  if (!formData.acceptTerms) {
    errors.acceptTerms = t ? t('errors.validation.acceptTerms') : 'You must accept the terms and conditions';
  }

  return errors;
};

export const validateLoginForm = (formData: {
  email: string;
  password: string;
}, t?: (key: string) => string): ValidationError => {
  const errors: ValidationError = {};

  // Username or email validation
  const usernameOrEmailError = validateUsernameOrEmail(formData.email, t);
  if (usernameOrEmailError) errors.email = usernameOrEmailError;

  // Password validation
  const passwordError = validateRequired(formData.password, 'Password', t);
  if (passwordError) errors.password = passwordError;

  return errors;
}; 